import type { Unit } from "@/lib/types/unit";
import { displayAirOptics, displayOptics } from "@/lib/utils/display-optics";
import { displayStealth } from "@/lib/utils/display-stealth";
import { formatDistance } from "@/lib/utils/format-distance";
import { formatTime } from "@/lib/utils/format-time";
import { formatNumber } from "@/lib/utils/format-number";

/**
 * Field configuration for unit fields
 * Defines display names and formatting functions for consistent field rendering
 */
export interface UnitFieldConfig {
  displayName: string;
  formatter: (value: unknown) => string;
}

/**
 * Type-safe field keys for unit fields
 */
export type UnitFieldKey = keyof Unit;

/**
 * Unit field display configuration
 */
export const UNIT_FIELD_CONFIG: Record<string, UnitFieldConfig> = {
  // Movement fields
  speed: {
    displayName: "Speed",
    formatter: (value) => `${formatNumber(value as number)} km/h`,
  },
  roadSpeed: {
    displayName: "Road Speed",
    formatter: (value) => `${formatNumber(value as number)} km/h`,
  },
  rotationTime: {
    displayName: "Turn Time",
    formatter: (value) => formatTime(value as number),
  },
  fuel: {
    displayName: "Fuel",
    formatter: (value) => formatNumber(value as number),
  },
  fuelMove: {
    displayName: "Fuel Move",
    formatter: (value) => formatNumber(value as number),
  },

  // Optics fields
  optics: {
    displayName: "Optics",
    formatter: (value) => displayOptics(value as number),
  },
  airOptics: {
    displayName: "Air Optics",
    formatter: (value) => displayAirOptics(value as number),
  },
  lowAltOptics: {
    displayName: "Low Alt Optics",
    formatter: (value) => displayOptics(value as number),
  },
  opticsRange: {
    displayName: "Optics Range",
    formatter: (value) => formatDistance(value as number | undefined),
  },
  airOpticsRange: {
    displayName: "Air Optics Range",
    formatter: (value) => formatDistance(value as number | undefined),
  },
  lowAltOpticsRange: {
    displayName: "Low Alt Optics Range",
    formatter: (value) => formatDistance(value as number | undefined),
  },

  // Other fields
  stealth: {
    displayName: "Stealth",
    formatter: (value) => displayStealth(value as number),
  },
  ecm: {
    displayName: "ECM",
    formatter: (value) => formatNumber(value as number),
  },
  supply: {
    displayName: "Supply",
    formatter: (value) => formatNumber(value as number),
  },
  advancedDeployment: {
    displayName: "Advanced Deployment",
    formatter: (value) => `${formatNumber(value as number)}m`,
  },
  maxDamage: {
    displayName: "Max Damage",
    formatter: (value) => formatNumber(value as number),
  },
  dangerousness: {
    displayName: "Dangerousness",
    formatter: (value) => formatNumber(value as number),
  },
  agility: {
    displayName: "Agility",
    formatter: (value) => formatNumber(value as number),
  },
  flyingAltitude: {
    displayName: "Flying Altitude",
    formatter: (value) => `${value}m`,
  },
  travelTime: {
    displayName: "Travel Time",
    formatter: (value) => formatTime(value as number | null),
  },
  bombStrategy: {
    displayName: "Bomb Strategy",
    formatter: (value) => String(value || ""),
  },
  commandPoints: {
    displayName: "CP",
    formatter: (value) => String(value),
  },
};

/**
 * Get display name for a unit field
 */
export function getUnitFieldDisplayName(fieldKey: string): string {
  return UNIT_FIELD_CONFIG[fieldKey]?.displayName || fieldKey;
}

/**
 * Format a unit field value
 */
export function formatUnitFieldValue(fieldKey: string, value: unknown): string {
  const config = UNIT_FIELD_CONFIG[fieldKey];
  if (!config) {
    return String(value ?? "-");
  }
  return config.formatter(value);
}

